import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TablePagination,
    Stack,
    TextField,
    InputAdornment,
    Skeleton,
    Button,
    LinearProgress,
    Badge
} from '@mui/material';
import { Search, FilterList, ArrowCircleUp, ArrowCircleDown, ReceiptLong } from '@mui/icons-material';
import { useVendorTransactions } from 'hooks/useTransactions';
import { useDebounce } from 'hooks/useDebounce';
import { format } from 'date-fns';
import FilterDrawer from 'components/common/FilterDrawer';

const VendorTransactions = () => {
    // --- UI & PAGINATION STATE ---
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // --- FILTER STATES ---
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    const debouncedSearch = useDebounce(searchTerm, 500);

    // --- DATA FETCHING ---
    const {
        data: responseData,
        isLoading,
        isFetching
    } = useVendorTransactions({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
        type: filters.type && filters.type !== 'ALL' ? filters.type : undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
    });

    const transactions = responseData?.data || [];
    const totalCount = responseData?.total || 0;

    // Reset page on filter change
    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, filters]);

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        setPage(0);
    };

    const filterConfig = [
        {
            name: 'type',
            label: 'Transaction Type',
            type: 'select',
            options: [
                { label: 'Credits (Earnings)', value: 'CREDIT' },
                { label: 'Debits (Payouts)', value: 'DEBIT' }
            ]
        },
        { name: 'startDate', label: 'Start Date', type: 'date' },
        { name: 'endDate', label: 'End Date', type: 'date' }
    ];

    const activeFiltersCount = Object.keys(filters).length;

    return (
        <Box p={3} sx={{ bgcolor: '#fbfbfb', minHeight: '100vh' }}>
            {/* HEADER */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <ReceiptLong sx={{ fontSize: 32, color: 'secondary.main' }} />
                    <Box>
                        <Typography variant="h4" fontWeight={800}>
                            Wallet Activity
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Detailed log of all credits and debits
                        </Typography>
                    </Box>
                </Box>
            </Stack>

            {/* FILTERS */}
            <Card sx={{ p: 2, mb: 3, borderRadius: 4, border: '1px solid #eee' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                    <TextField
                        fullWidth
                        placeholder="Search by reference or remark..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            )
                        }}
                        size="small"
                        sx={{ maxWidth: 500 }}
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Badge badgeContent={activeFiltersCount} color="primary">
                            <Button
                                variant="outlined"
                                startIcon={<FilterList />}
                                onClick={() => setFilterOpen(true)}
                                sx={{ borderRadius: 2, fontWeight: 700, minWidth: 100 }}
                            >
                                Filters
                            </Button>
                        </Badge>
                        {activeFiltersCount > 0 && (
                            <Button variant="text" onClick={() => setFilters({})} color="error" size="small">
                                Reset
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Card>

            <FilterDrawer
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                initialFilters={filters}
                config={filterConfig}
            />

            {/* TABLE */}
            <Card sx={{ borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                {isFetching && <LinearProgress sx={{ position: 'absolute', top: 0, width: '100%', height: 3 }} />}

                <TableContainer>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f1f3f4' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Transaction Date</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                Array(5)
                                    .fill(0)
                                    .map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={5}>
                                                <Skeleton height={50} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                            ) : transactions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No transactions found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                transactions.map((tx) => (
                                    <TableRow key={tx._id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {format(new Date(tx.createdAt), 'MMM dd, yyyy')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {format(new Date(tx.createdAt), 'p')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 0.5, borderRadius: 1 }}
                                            >
                                                {tx.reference}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {tx.type === 'CREDIT' ? (
                                                    <ArrowCircleDown color="success" sx={{ fontSize: 20 }} />
                                                ) : (
                                                    <ArrowCircleUp color="error" sx={{ fontSize: 20 }} />
                                                )}
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {tx.remark || (tx.type === 'CREDIT' ? 'Sales Earning' : 'Payout Debit')}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={800}
                                                color={tx.type === 'CREDIT' ? 'success.main' : 'error.main'}
                                            >
                                                {tx.type === 'CREDIT' ? '+' : '-'} ₦{tx.amount.toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={tx.status}
                                                size="small"
                                                color={tx.status === 'successful' ? 'success' : 'default'}
                                                sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Card>
        </Box>
    );
};

export default VendorTransactions;
